// @ts-check
// WHICH ROUTES A STRANGER MAY READ, and the one place that answers it.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHY THIS EXISTS AS A MODULE.
//
// AppRoutes takes the WHOLE TREE over on two conditions, above the router: the
// unmatched-actor screen and the "Cannot reach the Academy" panel. Both are right
// for the app and both are wrong for a public page — and the second one is not
// hypothetical. With the disconnected branch unscoped, a browser check of the
// built page showed exactly one thing on the client's front door when `/health`
// did not answer:
//
//   "Cannot reach the Academy — The server is not responding."
//
// The landing page has its own degradation path precisely so that an API failure
// costs a few sections rather than the whole page (screens/landing/Landing.jsx).
// A takeover mounted ABOVE the router defeats that completely, and a stranger
// evaluating the client's training offering sees an outage notice instead of the
// marketing page.
//
// Kept out of AppRoutes.jsx because a file that exports both components and
// helpers loses fast refresh — and because the list belongs beside
// config/navigation.js, not inside a component.
//
// THIS IS NOT AUTHORIZATION. The server refuses every call regardless of what is
// rendered. This list decides which client-side SHELL a route gets, nothing more.
// The authoritative anonymous surface is the server's AnonymousSurfaceTests.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Routes that render for somebody with no account and no session.
 *
 * All of them are anonymous for the same underlying reason: the person on the
 * other end has no account. For some of them — the employer checking a
 * certificate — that will never change; for the buyer at the checkout it is
 * the very thing they are arranging.
 *
 *   /          the marketing page — the client's front door
 *   /subscribe the membership checkout the marketing page's cards link to. A
 *              buyer has no account by definition — that is what they are
 *              here to arrange — and the takeover this list exists to scope
 *              would replace a page somebody is halfway through with an
 *              outage notice.
 *   /verify    an employer checking a certificate
 *   /for       one of the three audience doors — for businesses, for
 *              individuals, for universities (PLATFORM-PLAN-5 §5b.3)
 *   /curriculum one programme of study as its own page
 *   /program   one programme as its own page
 *
 *              The last three are anonymous for the front page's reason
 *              exactly: they are the pages it leads to, and a visitor who has
 *              to sign in to read what a programme teaches has been shown the
 *              door. They matter to THIS list specifically because they are
 *              the pages a SEARCH ENGINE indexes and a stranger lands on cold
 *              — the takeover this module exists to scope would put an outage
 *              notice on the deepest page of the client's catalogue, reached
 *              from a search result, with no front page in between.
 *   /about     the practice — the journey, the outcomes, the instructors, the
 *              portfolio and the resources, which were sections of the front
 *              page until it was cut to four
 *   /join      become a member: what it costs, and the ONE enquiry form on the
 *              site. On this list for the checkout's reason rather than the
 *              catalogue's — the person reading a price has no account and is
 *              deciding whether to open one, so an outage notice here is an
 *              outage notice on the page that closes the sale.
 *   /membership the same page's former address, kept as a redirect because it
 *              was live for a deploy and may be bookmarked.
 *
 *              BOTH ARE BARE PATHS, not prefixes — they take no parameter, so
 *              they belong in PUBLIC_PATHS and must NOT be added below, where
 *              `/membership/` would open a prefix nothing needs.
 *   /register  the public LEAD form. It was the admission intake; since the
 *              admission queue was deleted (PLATFORM-PLAN WS7) it posts to the
 *              CRM like the landing page's enquiry form. Still anonymous by
 *              definition — the person filling it in has no account.
 */
export const PUBLIC_PATHS = ['/', '/subscribe', '/verify', '/about', '/join', '/membership'];

/**
 * The public routes that take a parameter, matched by PREFIX below.
 *
 * A SEPARATE LIST FROM PUBLIC_PATHS, because these are never bare: `/program`
 * with no code is not a page, and putting it in the list above would make the
 * bare path public as well as the parameterised one.
 *
 * `/screen/` is HR's screening test (SKILL-ASSESSMENT-PLAN §5.4): a candidate
 * with no account, halfway through a timed test — the last person who should
 * see an outage takeover in place of the page whose autosave would recover.
 *
 * EVERY ENTRY ENDS IN A SLASH, and that is the control rather than a
 * formatting habit — `/program/` cannot match `/programs`, which is the STAFF
 * catalogue screen one letter away.
 */
const PUBLIC_PREFIXES = [
    '/verify/', '/subscribe/', '/for/', '/curriculum/', '/program/', '/screen/',
];

/**
 * Whether a pathname is one of the public routes.
 *
 * PREFIX MATCHING IS DELIBERATELY NARROW: only the entries in
 * PUBLIC_PREFIXES are matched by prefix, and every one of them ends in a
 * SLASH. That trailing slash is the whole control: `/program/` cannot match
 * `/programs`, which is the staff catalogue screen. A blanket
 * `startsWith` over the list used to make `/registrations` — the STAFF QUEUE —
 * public, because it shares a prefix with `/register`. That screen is gone, and
 * the rule stays: the next staff route whose path begins with a public one
 * would be served the public shell to strangers, and nothing else checks.
 *
 * @param {string} pathname
 */
export const isPublicPath = (pathname) =>
    PUBLIC_PATHS.includes(pathname)
    || PUBLIC_PREFIXES.some((prefix) => pathname.startsWith(prefix));
